(() => {
  const normalize = (value) =>
    String(value || "")
      .toLocaleLowerCase("pl-PL")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ł/g, "l")
      .trim();

  const termWord = (count) => {
    if (count === 1) return "hasło";

    const lastTwo = count % 100;
    const last = count % 10;

    if (last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14)) {
      return "hasła";
    }

    return "haseł";
  };

  const prefersReducedMotion = () =>
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const initGlossary = (root) => {
    if (!root || root.dataset.pglInitialized === "true") return;

    root.dataset.pglInitialized = "true";

    const searchInput = root.querySelector("[data-pgl-search]");
    const clearButton = root.querySelector("[data-pgl-clear]");
    const counter = root.querySelector("[data-pgl-counter]");
    const totalTargets = root.querySelectorAll("[data-pgl-total]");
    const noResults = root.querySelector("[data-pgl-no-results]");
    const alphabet = root.querySelector("[data-pgl-alphabet]");
    const randomButton = root.querySelector("[data-pgl-random]");
    const directory = root.querySelector(".pgl-directory");
    const groups = [...root.querySelectorAll("[data-pgl-letter-group]")];
    const categoryButtons = [
      ...root.querySelectorAll("[data-pgl-category-filter]"),
    ];
    const resetButtons = [...root.querySelectorAll("[data-pgl-reset]")];

    // Shopify/Liquid sorting is not fully locale-aware for Polish diacritics.
    // Normalize the rendered catalogue order in the browser using Polish collation.
    const grid = root.querySelector("[data-pgl-grid]");
    if (grid) {
      [...grid.querySelectorAll("[data-pgl-term]")]
        .sort((a, b) =>
          String(a.dataset.name || "").localeCompare(
            String(b.dataset.name || ""),
            "pl",
            { sensitivity: "base" }
          )
        )
        .forEach((card) => grid.appendChild(card));
    }

    const cards = [...root.querySelectorAll("[data-pgl-term]")];

    const total = cards.length;
    let activeCategory = "all";
    let activeLetter = "all";
    let inputFrame = 0;
    let highlightTimer = 0;

    cards.forEach((card) => {
      card.pglSearchValue = normalize(
        `${card.dataset.name || ""} ${card.dataset.search || ""}`
      );
      card.pglLetter = normalize(card.dataset.letter || "")
        .slice(0, 1)
        .toUpperCase();
    });

    totalTargets.forEach((target) => {
      target.textContent = String(total);
    });

    categoryButtons.forEach((button) => {
      const category = button.dataset.pglCategoryFilter;
      const count =
        category === "all"
          ? total
          : cards.filter((card) => card.dataset.category === category).length;

      const countTarget = button.querySelector("[data-pgl-filter-count]");
      if (countTarget) countTarget.textContent = String(count);

      if (category !== "all" && count === 0) {
        button.disabled = true;
        button.hidden = true;
      }
    });

    const buildAlphabet = () => {
      if (!alphabet) return;

      const letters = [
        ...new Set(cards.map((card) => card.pglLetter).filter(Boolean)),
      ].sort((a, b) => a.localeCompare(b, "pl", { sensitivity: "base" }));

      letters.forEach((letter) => {
        const button = document.createElement("button");
        button.className = "pgl-letter";
        button.type = "button";
        button.dataset.pglLetterFilter = letter;
        button.setAttribute("aria-pressed", "false");
        button.textContent = letter;
        alphabet.appendChild(button);
      });
    };

    buildAlphabet();

    const letterButtons = [
      ...root.querySelectorAll("[data-pgl-letter-filter]"),
    ];

    const setPressed = (buttons, activeButton) => {
      buttons.forEach((button) => {
        const active = button === activeButton;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });
    };

    const getVisibleCards = () => cards.filter((card) => !card.hidden);

    const updateGroups = () => {
      groups.forEach((group) => {
        const hasVisibleCard = [...group.querySelectorAll("[data-pgl-term]")].some(
          (card) => !card.hidden
        );
        group.hidden = !hasVisibleCard;
      });
    };

    const update = () => {
      const query = normalize(searchInput?.value);
      let visibleCount = 0;

      cards.forEach((card) => {
        const matchesCategory =
          activeCategory === "all" || card.dataset.category === activeCategory;

        const matchesLetter =
          activeLetter === "all" || card.pglLetter === activeLetter;

        const matchesQuery = !query || card.pglSearchValue.includes(query);
        const visible = matchesCategory && matchesLetter && matchesQuery;

        card.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      updateGroups();

      if (counter) {
        const totalWord = total === 1 ? "hasła" : "haseł";
        counter.textContent = `${visibleCount} z ${total} ${totalWord}`;
      }

      if (clearButton) clearButton.hidden = !query;

      const filtersActive =
        Boolean(query) || activeCategory !== "all" || activeLetter !== "all";

      resetButtons.forEach((button) => {
        if (!button.closest("[data-pgl-no-results]")) {
          button.hidden = !filtersActive;
        }
      });

      if (noResults) noResults.hidden = visibleCount !== 0;
      if (directory) directory.classList.toggle("is-empty", visibleCount === 0);

      root.dataset.pglVisible = String(visibleCount);
    };

    const resetAll = ({ focus = true } = {}) => {
      activeCategory = "all";
      activeLetter = "all";

      if (searchInput) searchInput.value = "";

      const allCategory = categoryButtons.find(
        (button) => button.dataset.pglCategoryFilter === "all"
      );
      const allLetter = letterButtons.find(
        (button) => button.dataset.pglLetterFilter === "all"
      );

      if (allCategory) setPressed(categoryButtons, allCategory);
      if (allLetter) setPressed(letterButtons, allLetter);

      update();

      if (focus && searchInput) {
        searchInput.focus({ preventScroll: true });
      }
    };

    const highlightCard = (card) => {
      if (!card) return;

      window.clearTimeout(highlightTimer);
      cards.forEach((item) => item.classList.remove("is-pgl-highlighted"));
      card.classList.add("is-pgl-highlighted");

      card.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "center",
      });

      const link = card.querySelector("a[href]");
      window.setTimeout(() => link?.focus({ preventScroll: true }), 350);

      highlightTimer = window.setTimeout(() => {
        card.classList.remove("is-pgl-highlighted");
      }, 2600);
    };

    searchInput?.addEventListener("input", () => {
      cancelAnimationFrame(inputFrame);
      inputFrame = requestAnimationFrame(update);
    });

    searchInput?.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && searchInput.value) {
        searchInput.value = "";
        update();
      }
    });

    clearButton?.addEventListener("click", () => {
      if (!searchInput) return;
      searchInput.value = "";
      update();
      searchInput.focus();
    });

    categoryButtons.forEach((button) => {
      button.addEventListener("click", () => {
        if (button.disabled) return;
        activeCategory = button.dataset.pglCategoryFilter || "all";
        setPressed(categoryButtons, button);
        update();
      });
    });

    letterButtons.forEach((button) => {
      button.addEventListener("click", () => {
        activeLetter = button.dataset.pglLetterFilter || "all";
        setPressed(letterButtons, button);
        update();
      });
    });

    resetButtons.forEach((button) => {
      button.addEventListener("click", () => resetAll());
    });

    randomButton?.addEventListener("click", () => {
      let pool = getVisibleCards();

      if (!pool.length) {
        resetAll({ focus: false });
        pool = cards;
      }

      if (!pool.length) return;

      const randomIndex = Math.floor(Math.random() * pool.length);
      highlightCard(pool[randomIndex]);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      const activeElement = document.activeElement;
      const tagName = activeElement?.tagName?.toLowerCase();
      const isTyping =
        tagName === "input" || tagName === "textarea" || activeElement?.isContentEditable;

      if (isTyping || !searchInput) return;

      event.preventDefault();
      searchInput.focus();
    });

    update();
  };

  const initAll = () => {
    document.querySelectorAll("[data-pgl-root]").forEach(initGlossary);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initGlossary(event.target.querySelector("[data-pgl-root]"));
  });
})();
